import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
  Divider,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { format as formatDate } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

const TimeEntryDetails = ({ 
  open, 
  onClose, 
  timeEntry, 
  projectLookup = {}, 
  clientLookup = {},
  onEdit,
  displayTimezone = 'UTC' // Timezone to display times in (should match header timezone)
}) => {
  if (!timeEntry) return null;

  const project = projectLookup[timeEntry.project];
  const client = project ? Object.values(clientLookup).find(c => c.id === project.client) : null;

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatAmount = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount || 0);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '12px',
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between' 
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Time Entry Details
        </Typography>
        <Box>
          <IconButton 
            size="small" 
            onClick={() => onEdit && onEdit(timeEntry)}
            sx={{ mr: 1 }}
          >
            <EditIcon />
          </IconButton>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 1 }}>
        <Grid container spacing={2}>
          {/* Description */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Description
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {timeEntry.description || 'No description'}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Member and Status */}
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Member
            </Typography>
            <Typography variant="body2">
              {timeEntry.member_username}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Status
            </Typography>
            <Chip 
              label={timeEntry.status || 'pending'} 
              size="small"
              color={
                (timeEntry.status === 'approved' && 'success') ||
                (timeEntry.status === 'rejected' && 'error') ||
                'warning'
              }
            />
          </Grid>

          {/* Project and Client */}
          {project && (
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Project
              </Typography>
              <Typography variant="body2">
                {project.name || project.full_project_name}
              </Typography>
            </Grid>
          )}
          {client && (
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Client
              </Typography>
              <Typography variant="body2">
                {client.name} ({client.code})
              </Typography>
            </Grid>
          )}

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Time Information */}
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Start Time
            </Typography>
            <Typography variant="body2">
              {formatInTimeZone(new Date(timeEntry.start_time), displayTimezone, 'MMM d, yyyy h:mm a')}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              End Time
            </Typography>
            <Typography variant="body2">
              {timeEntry.end_time ? formatInTimeZone(new Date(timeEntry.end_time), displayTimezone, 'MMM d, yyyy h:mm a') : 'Not ended'}
            </Typography>
          </Grid>

          {/* Duration and Amount */}
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Duration
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {formatDuration(timeEntry.duration)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Amount
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {formatAmount(timeEntry.amount, timeEntry.currency)}
            </Typography>
          </Grid>

          {/* Billable Status */}
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Billable
            </Typography>
            <Chip 
              label={timeEntry.billable ? 'Yes' : 'No'} 
              size="small"
              color={timeEntry.billable ? 'success' : 'warning'}
              variant={timeEntry.billable ? 'filled' : 'outlined'}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Rate
            </Typography>
            <Typography variant="body2">
              {formatAmount(timeEntry.rate, timeEntry.currency)}/hour
            </Typography>
          </Grid>

          {/* Tags */}
          {timeEntry.tags && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Tags
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {timeEntry.tags.split(',').map((tag, index) => (
                  <Chip key={index} label={tag.trim()} size="small" variant="outlined" />
                ))}
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{  px: 3, pb: 2 }}>
        <Button sx={{bgcolor:'#6b95d1'}} onClick={onClose}>Close</Button>
        {onEdit && (
          <Button 
            variant="contained" 
            startIcon={<EditIcon />}
            onClick={() => onEdit(timeEntry)}
            sx={{ 
              bgcolor: '#4CAF50', 
              '&:hover': { bgcolor: '#45A049' }
            }}
          >
            Edit Entry
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TimeEntryDetails;
